import {
  Check,
  ChevronDown,
  ChevronRight,
  Download,
  Eye,
  EyeOff,
  FileCheck2,
  FilePlus2,
  FileScan,
  FileText,
  Info,
  Layers,
  LoaderCircle,
  MousePointer2,
  Move,
  PanelRight,
  Plus,
  Redo2,
  RotateCw,
  ScanText,
  Search,
  Trash2,
  Type,
  Undo2,
  Upload,
  ZoomIn,
  ZoomOut,
} from "lucide-react";
import type { PDFDocumentProxy } from "pdfjs-dist";
import { useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type WheelEvent as ReactWheelEvent } from "react";
import { loadPdf, renderPageForOcr, renderPdfPage } from "./pdf";
import { aggregateFonts, aggregateOcrFonts } from "./pdfAnalysis";
import { fitFontSizeToBox } from "./textFit";
import { getUiCopy, INTERFACE_LANGUAGE_STORAGE_KEY, type InterfaceLanguage, type UiCopy } from "./i18n";
import {
  clearStoredWorkspace,
  loadStoredWorkspace,
  saveStoredEditorState,
  saveStoredPdf,
  type StoredEditorState,
} from "./editorPersistence";
import type {
  EditorSnapshot,
  EditorTool,
  FontUsage,
  LoadedPdf,
  OcrProgress,
  PdfEdit,
  PdfPageInfo,
  TextOverlay,
} from "./types";

const EMPTY_SNAPSHOT: EditorSnapshot = { edits: [], deletedPages: [] };
const makeId = () => crypto.randomUUID();
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));
const closestEditableFont = (family: string): NonNullable<PdfEdit["fontFamily"]> => {
  if (/courier|mono/i.test(family)) return "Courier";
  if (/times|serif|georgia|garamond|cambria/i.test(family)) return "Liberation Serif";
  if (/arial/i.test(family)) return "Arial";
  if (/liberation.?sans/i.test(family)) return "Liberation Sans";
  if (/helvetica/i.test(family)) return "Helvetica";
  if (/sans/i.test(family)) return "Liberation Sans";
  return "Inter";
};
const fontFamilyCss = (family?: PdfEdit["fontFamily"], embeddedFontFamily?: string) => {
  if (embeddedFontFamily) return `"${embeddedFontFamily}", Arial, sans-serif`;
  if (family === "Arial") return 'Arial, "Liberation Sans", Helvetica, sans-serif';
  if (family === "Liberation Sans") return '"Liberation Sans", Arial, Helvetica, sans-serif';
  if (family === "Liberation Serif") return '"Liberation Serif", "Times New Roman", serif';
  if (family === "Times Roman") return '"Times New Roman", Times, serif';
  if (family === "Courier") return 'Courier, "Courier New", monospace';
  if (family === "Helvetica") return 'Helvetica, Arial, sans-serif';
  return 'Inter, Arial, sans-serif';
};

let fontMeasureCanvas: HTMLCanvasElement | null = null;
const measuredFontSize = ({
  text,
  width,
  height,
  preferredSize,
  fontFamily,
  fontWeight,
  fontStyle,
  embeddedFontFamily,
}: {
  text: string;
  width: number;
  height: number;
  preferredSize: number;
  fontFamily?: PdfEdit["fontFamily"];
  fontWeight?: PdfEdit["fontWeight"];
  fontStyle?: PdfEdit["fontStyle"];
  embeddedFontFamily?: string;
}) => {
  if (typeof document === "undefined") return fitFontSizeToBox({ text, width, height, preferredSize });
  fontMeasureCanvas ??= document.createElement("canvas");
  const context = fontMeasureCanvas.getContext("2d");
  if (!context) return fitFontSizeToBox({ text, width, height, preferredSize });
  return fitFontSizeToBox({
    text,
    width,
    height,
    preferredSize,
    measureTextWidth: (value, size) => {
      context.font = `${fontStyle === "italic" ? "italic " : ""}${fontWeight || 400} ${size}px ${fontFamilyCss(fontFamily, embeddedFontFamily)}`;
      return context.measureText(value).width;
    },
  });
};

const ocrTypographyPatch = (source: TextOverlay, width: number, height: number): Partial<PdfEdit> => {
  const fontFamily = closestEditableFont(source.fontFamily);
  const preferredFontSize = Math.max(6, source.fontSize);
  const fontWeight = source.fontWeight || 400;
  const fontStyle = source.fontStyle || "normal";
  return {
    fontFamily,
    fontWeight,
    fontStyle,
    preferredFontSize,
    fontSize: measuredFontSize({ text: source.text, width, height, preferredSize: preferredFontSize, fontFamily, fontWeight, fontStyle }),
    fontAutoMatch: true,
    fontMatchConfidence: source.fontMatchConfidence,
    autoFit: true,
  };
};

const nativeTypographyPatch = (source: TextOverlay, width: number, height: number): Partial<PdfEdit> => {
  const fontFamily = closestEditableFont(source.fontFamily);
  const preferredFontSize = Math.max(5, source.fontSize);
  const fontWeight = source.fontWeight || 400;
  const fontStyle = source.fontStyle || "normal";
  return {
    fontFamily,
    fontWeight,
    fontStyle,
    embeddedFontId: source.embeddedFontId,
    embeddedFontFamily: source.embeddedFontFamily,
    preferredFontSize,
    fontSize: measuredFontSize({
      text: source.text,
      width,
      height,
      preferredSize: preferredFontSize,
      fontFamily,
      fontWeight,
      fontStyle,
      embeddedFontFamily: source.embeddedFontFamily,
    }),
    fontAutoMatch: true,
    fontMatchConfidence: source.embeddedFontId ? 1 : 0.88,
    autoFit: true,
  };
};

interface IconButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  label: string;
  active?: boolean;
}

function IconButton({ label, active, className = "", children, ...props }: IconButtonProps) {
  return (
    <button
      type="button"
      className={`icon-button ${active ? "is-active" : ""} ${className}`}
      aria-label={label}
      title={label}
      {...props}
    >
      {children}
    </button>
  );
}

function StatusPill({ children, tone = "neutral" }: { children: React.ReactNode; tone?: "neutral" | "green" | "amber" }) {
  return <span className={`status-pill status-${tone}`}>{children}</span>;
}

function Thumbnail({ document, page, active, deleted, onClick, ui }: {
  document: PDFDocumentProxy;
  page: PdfPageInfo;
  active: boolean;
  deleted: boolean;
  onClick: () => void;
  ui: UiCopy;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    if (!canvasRef.current || deleted) return;
    const controller = new AbortController();
    const scale = Math.min(0.18, 126 / page.width);
    void renderPdfPage(document, page.index, canvasRef.current, scale, controller.signal);
    return () => controller.abort();
  }, [deleted, document, page]);
  return (
    <button
      type="button"
      className={`page-thumbnail ${active ? "is-active" : ""} ${deleted ? "is-deleted" : ""}`}
      onClick={onClick}
      aria-label={ui.page(page.index + 1)}
    >
      <div className="thumbnail-paper">{!deleted && <canvas ref={canvasRef} />}{deleted && <Trash2 />}</div>
      <span>{page.index + 1}</span>
      {page.isScanned && !deleted && <small><FileScan /> {ui.scan}</small>}
    </button>
  );
}

interface PageCanvasProps {
  document: PDFDocumentProxy;
  page: PdfPageInfo;
  zoom: number;
  tool: EditorTool;
  edits: PdfEdit[];
  ocrItems: TextOverlay[];
  selectedEditId: string | null;
  onTextSource: (item: TextOverlay) => void;
  onSelectEdit: (id: string | null) => void;
  onCreateEdit: (edit: PdfEdit) => void;
  onUpdateEdit: (id: string, patch: Partial<PdfEdit>) => void;
  ui: UiCopy;
}

type TransformMode = "move" | "resize-nw" | "resize-ne" | "resize-se" | "resize-sw" | "rotate";

interface TransformSession {
  id: string;
  pointerId: number;
  mode: TransformMode;
  startClientX: number;
  startClientY: number;
  centerClientX: number;
  centerClientY: number;
  startAngle: number;
  initial: PdfEdit;
}

interface MiddlePanSession {
  pointerId: number;
  startClientX: number;
  startClientY: number;
  startScrollLeft: number;
  startScrollTop: number;
}

function PageCanvas({
  document,
  page,
  zoom,
  tool,
  edits,
  ocrItems,
  selectedEditId,
  onTextSource,
  onSelectEdit,
  onCreateEdit,
  onUpdateEdit,
  ui,
}: PageCanvasProps) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [transformDraft, setTransformDraft] = useState<PdfEdit | null>(null);
  const transformDraftRef = useRef<PdfEdit | null>(null);
  const transformSession = useRef<TransformSession | null>(null);
  const dragStart = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!canvasRef.current) return;
    const controller = new AbortController();
    void renderPdfPage(document, page.index, canvasRef.current, zoom, controller.signal);
    return () => controller.abort();
  }, [document, page.index, zoom]);

  const position = (event: ReactPointerEvent<HTMLDivElement>) => {
    const rect = event.currentTarget.getBoundingClientRect();
    return {
      x: clamp((event.clientX - rect.left) / zoom, 0, page.width),
      y: clamp((event.clientY - rect.top) / zoom, 0, page.height),
    };
  };

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.target !== event.currentTarget && (event.target as Element).closest(".edit-overlay, .text-hit-area")) return;
    const point = position(event);
    if (tool === "select") {
      onSelectEdit(null);
      return;
    }
    if (tool === "text") {
      onCreateEdit({
        id: makeId(), pageIndex: page.index, kind: "text", x: point.x, y: point.y,
        width: Math.min(220, page.width - point.x), height: 30, text: ui.newText,
        fontFamily: "Inter", fontSize: 14, preferredFontSize: 14, fontWeight: 400, fontStyle: "normal", color: "#111318", opacity: 1,
        source: "new", maskOriginal: false, autoFit: true,
      });
      return;
    }
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStart.current = point;
    setDraft({ ...point, width: 0, height: 0 });
  };

  const handlePointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (!dragStart.current || (tool !== "highlight" && tool !== "redact")) return;
    const point = position(event);
    setDraft({
      x: Math.min(dragStart.current.x, point.x),
      y: Math.min(dragStart.current.y, point.y),
      width: Math.abs(point.x - dragStart.current.x),
      height: Math.abs(point.y - dragStart.current.y),
    });
  };

  const handlePointerUp = () => {
    if (draft && draft.width > 3 && draft.height > 3 && (tool === "highlight" || tool === "redact")) {
      onCreateEdit({
        id: makeId(), pageIndex: page.index, kind: tool, ...draft,
        color: tool === "highlight" ? "#ffe06a" : "#111111",
        opacity: tool === "highlight" ? 0.42 : 1,
      });
    }
    dragStart.current = null;
    setDraft(null);
  };

  const beginTransform = (event: ReactPointerEvent<HTMLElement>, edit: PdfEdit, mode: TransformMode) => {
    if (event.button !== 0 || !overlayRef.current) return;
    event.preventDefault();
    event.stopPropagation();
    const shell = (event.target as Element).closest(".transform-shell") as HTMLElement | null;
    shell?.setPointerCapture(event.pointerId);
    const overlayRect = overlayRef.current.getBoundingClientRect();
    const centerClientX = overlayRect.left + (edit.x + edit.width / 2) * zoom;
    const centerClientY = overlayRect.top + (edit.y + edit.height / 2) * zoom;
    transformSession.current = {
      id: edit.id,
      pointerId: event.pointerId,
      mode,
      startClientX: event.clientX,
      startClientY: event.clientY,
      centerClientX,
      centerClientY,
      startAngle: Math.atan2(event.clientY - centerClientY, event.clientX - centerClientX),
      initial: edit,
    };
    transformDraftRef.current = edit;
    setTransformDraft(edit);
  };

  const handleTransformPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const session = transformSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    const initial = session.initial;
    const dx = (event.clientX - session.startClientX) / zoom;
    const dy = (event.clientY - session.startClientY) / zoom;
    let next: PdfEdit = { ...initial };

    if (session.mode === "move") {
      next.x = clamp(initial.x + dx, 0, Math.max(0, page.width - initial.width));
      next.y = clamp(initial.y + dy, 0, Math.max(0, page.height - initial.height));
    } else if (session.mode === "rotate") {
      const angle = Math.atan2(event.clientY - session.centerClientY, event.clientX - session.centerClientX);
      const degrees = (initial.rotation || 0) + (angle - session.startAngle) * 180 / Math.PI;
      next.rotation = ((degrees + 180) % 360 + 360) % 360 - 180;
    } else {
      const angle = (initial.rotation || 0) * Math.PI / 180;
      const localDx = dx * Math.cos(angle) + dy * Math.sin(angle);
      const localDy = -dx * Math.sin(angle) + dy * Math.cos(angle);
      const left = session.mode.endsWith("nw") || session.mode.endsWith("sw");
      const top = session.mode.endsWith("nw") || session.mode.endsWith("ne");
      const signX = left ? -1 : 1;
      const signY = top ? -1 : 1;
      const width = Math.max(12, initial.width + signX * localDx);
      const height = Math.max(10, initial.height + signY * localDy);
      const effectiveLocalDx = signX * (width - initial.width);
      const effectiveLocalDy = signY * (height - initial.height);
      const centerShiftX = (effectiveLocalDx * Math.cos(angle) - effectiveLocalDy * Math.sin(angle)) / 2;
      const centerShiftY = (effectiveLocalDx * Math.sin(angle) + effectiveLocalDy * Math.cos(angle)) / 2;
      const centerX = initial.x + initial.width / 2 + centerShiftX;
      const centerY = initial.y + initial.height / 2 + centerShiftY;
      next = { ...initial, width, height, x: centerX - width / 2, y: centerY - height / 2 };
    }

    transformDraftRef.current = next;
    setTransformDraft(next);
  };

  const finishTransform = (event: ReactPointerEvent<HTMLDivElement>) => {
    const session = transformSession.current;
    if (!session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    const next = transformDraftRef.current;
    if (next) {
      onUpdateEdit(session.id, {
        x: next.x,
        y: next.y,
        width: next.width,
        height: next.height,
        rotation: next.rotation || 0,
      });
    }
    transformSession.current = null;
    transformDraftRef.current = null;
    setTransformDraft(null);
  };

  const pageEdits = edits.filter((edit) => edit.pageIndex === page.index && edit.hidden !== true);
  const sourceItems = [...page.textItems, ...ocrItems];
  const foregroundImage = (edit: PdfEdit) => edit.foregroundImageDataUrl
    ? <img className="text-foreground-overlay" src={edit.foregroundImageDataUrl} alt="" aria-hidden="true" />
    : null;
  return (
    <div className={`page-paper tool-${tool}`} style={{ width: page.width * zoom, height: page.height * zoom }}>
      <canvas ref={canvasRef} />
      <div
        ref={overlayRef}
        className="page-overlay"
        style={{ width: page.width * zoom, height: page.height * zoom }}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
      >
        {tool === "select" && sourceItems.map((item) => (
          <button
            type="button"
            className={`text-hit-area ${item.source === "ocr" ? "is-ocr" : ""}`}
            key={item.id}
            aria-label={item.text}
            title={item.source === "ocr" ? `OCR ${Math.round(item.confidence || 0)}%` : `${item.fontFamily} · ${Math.round(item.fontSize)} pt`}
            style={{
              left: item.x * zoom,
              top: item.y * zoom,
              width: Math.max(3, item.width * zoom),
              height: Math.max(8, item.height * zoom),
            }}
            onPointerDown={(event) => event.stopPropagation()}
            onClick={(event) => { event.stopPropagation(); onTextSource(item); }}
          />
        ))}
        {pageEdits.map((edit) => {
          const selected = edit.id === selectedEditId;
          const visibleEdit = transformDraft?.id === edit.id ? transformDraft : edit;
          const style = {
            left: visibleEdit.x * zoom,
            top: visibleEdit.y * zoom,
            width: visibleEdit.width * zoom,
            height: visibleEdit.height * zoom,
            transform: `rotate(${visibleEdit.rotation || 0}deg)`,
          };
          if (selected) {
            return (
              <div
                key={edit.id}
                className={`edit-overlay transform-shell is-selected transform-${edit.kind}`}
                style={style}
                onPointerDown={(event) => beginTransform(event, visibleEdit, "move")}
                onPointerMove={handleTransformPointerMove}
                onPointerUp={finishTransform}
                onPointerCancel={finishTransform}
              >
                {edit.kind === "text" ? (
                  <>
                    <textarea
                      autoFocus
                      className="transform-content edit-text"
                      style={{ color: edit.color, fontSize: (edit.fontSize || 14) * zoom, fontFamily: fontFamilyCss(edit.fontFamily, edit.embeddedFontFamily), fontWeight: edit.fontWeight || 400, fontStyle: edit.fontStyle || "normal" }}
                      value={edit.text || ""}
                      onPointerDown={(event) => event.stopPropagation()}
                      onChange={(event) => onUpdateEdit(edit.id, { text: event.target.value })}
                    />
                    {foregroundImage(edit)}
                  </>
                ) : <div className={`transform-content edit-rect edit-${edit.kind}`} style={{ background: edit.color, opacity: edit.opacity }} />}
                <button type="button" className="transform-move-handle" aria-label={ui.moveLayer} onPointerDown={(event) => beginTransform(event, visibleEdit, "move")}><Move /></button>
                {(["nw", "ne", "se", "sw"] as const).map((corner) => <button type="button" key={corner} className={`transform-handle handle-${corner}`} aria-label={ui.resizeLayer(corner)} onPointerDown={(event) => beginTransform(event, visibleEdit, `resize-${corner}`)} />)}
                <button type="button" className="transform-rotate-handle" aria-label={ui.rotateLayer} onPointerDown={(event) => beginTransform(event, visibleEdit, "rotate")}><RotateCw /></button>
              </div>
            );
          }
          if (edit.kind === "text") return (
            <button
              type="button"
              key={edit.id}
              className="edit-overlay edit-text"
              style={{ ...style, color: edit.color, fontSize: (edit.fontSize || 14) * zoom, fontFamily: fontFamilyCss(edit.fontFamily, edit.embeddedFontFamily), fontWeight: edit.fontWeight || 400, fontStyle: edit.fontStyle || "normal" }}
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => { event.stopPropagation(); onSelectEdit(edit.id); }}
            ><span className="edit-text-value">{edit.text}</span>{foregroundImage(edit)}</button>
          );
          return (
            <button type="button" key={edit.id} className={`edit-overlay edit-rect edit-${edit.kind}`} style={{ ...style, background: edit.color, opacity: edit.opacity }} onPointerDown={(event) => event.stopPropagation()} onClick={(event) => { event.stopPropagation(); onSelectEdit(edit.id); }} aria-label={edit.kind === "highlight" ? ui.highlight : ui.redaction} />
          );
        })}
        {draft && <div className={`draft-rect draft-${tool}`} style={{ left: draft.x * zoom, top: draft.y * zoom, width: draft.width * zoom, height: draft.height * zoom }} />}
      </div>
    </div>
  );
}

function LayerList({
  edits,
  pageIndex,
  selectedEditId,
  onSelect,
  onToggleVisibility,
  ui,
}: {
  edits: PdfEdit[];
  pageIndex: number;
  selectedEditId: string | null;
  onSelect: (edit: PdfEdit) => void;
  onToggleVisibility: (edit: PdfEdit) => void;
  ui: UiCopy;
}) {
  const layers = [...edits.filter((edit) => edit.pageIndex === pageIndex)].reverse();
  return (
    <div className="layers-pane">
      <div className="layers-heading"><Layers /><span>{ui.page(pageIndex + 1)}</span><strong>{layers.length}</strong></div>
      <div className="layer-list" aria-label={ui.pageLayers}>
        {layers.map((edit) => {
          const label = edit.kind === "text" ? ui.text : edit.kind === "highlight" ? ui.colorLayer : ui.redaction;
          return (
            <div className={`layer-row ${edit.id === selectedEditId ? "is-selected" : ""} ${edit.hidden ? "is-hidden" : ""}`} key={edit.id}>
              <button type="button" className="layer-main" onClick={() => onSelect(edit)}>
                <span className={`layer-thumbnail layer-${edit.kind}`}>
                  <span style={{ backgroundColor: edit.color, opacity: edit.opacity }} />
                  {edit.kind === "text" && <b>T</b>}
                </span>
                <span className="layer-copy"><strong>{label}</strong><small>{edit.kind === "text" ? (edit.text || ui.emptyText) : `${edit.color.toUpperCase()} · ${Math.round(edit.opacity * 100)}%`}</small></span>
              </button>
              <button
                type="button"
                className="layer-visibility"
                aria-label={ui.language === "sk" ? `${edit.hidden ? ui.show : ui.hide} vrstvu ${label}` : `${edit.hidden ? ui.show : ui.hide} layer ${label}`}
                title={ui.language === "sk" ? `${edit.hidden ? ui.show : ui.hide} vrstvu` : `${edit.hidden ? ui.show : ui.hide} layer`}
                onClick={() => onToggleVisibility(edit)}
              >{edit.hidden ? <EyeOff /> : <Eye />}</button>
            </div>
          );
        })}
        {layers.length === 0 && <div className="empty-layers"><Layers /><strong>{ui.noLayers}</strong><span>{ui.noLayersHint}</span></div>}
      </div>
    </div>
  );
}

function Welcome({ onOpen, busy, ui }: { onOpen: () => void; busy: boolean; ui: UiCopy }) {
  return (
    <main className="welcome-stage">
      <div className="welcome-card">
        <div className="product-mark"><FileText /><span>PDF</span></div>
        <div className="welcome-actions">
          <button type="button" className="primary-button" onClick={onOpen} disabled={busy}><Upload /> {ui.openPdf}</button>
        </div>
      </div>
    </main>
  );
}

export default function App() {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const stageScrollRef = useRef<HTMLDivElement>(null);
  const middlePanSessionRef = useRef<MiddlePanSession | null>(null);
  const autoOcrStartedRef = useRef(new Set<number>());
  const restoreStartedRef = useRef(false);
  const clearWorkspacePromiseRef = useRef<Promise<void> | null>(null);
  const [language, setLanguage] = useState<InterfaceLanguage>(() => {
    try {
      return localStorage.getItem(INTERFACE_LANGUAGE_STORAGE_KEY) === "en" ? "en" : "sk";
    } catch {
      return "sk";
    }
  });
  const ui = useMemo(() => getUiCopy(language), [language]);
  const [pdf, setPdf] = useState<LoadedPdf | null>(null);
  const [persistenceReady, setPersistenceReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [message, setMessage] = useState(ui.ready);
  const [activePage, setActivePage] = useState(0);
  const [zoom, setZoom] = useState(0.82);
  const [tool, setTool] = useState<EditorTool>("select");
  const [history, setHistory] = useState<EditorSnapshot[]>([EMPTY_SNAPSHOT]);
  const [historyIndex, setHistoryIndex] = useState(0);
  const [selectedEditId, setSelectedEditId] = useState<string | null>(null);
  const [rightTab, setRightTab] = useState<"edit" | "fonts" | "ocr">("edit");
  const [ocrByPage, setOcrByPage] = useState<Record<number, TextOverlay[]>>({});
  const [ocrProgress, setOcrProgress] = useState<OcrProgress | null>(null);
  const [ocrLanguages, setOcrLanguages] = useState(["slk", "ces", "eng"]);
  const [autoOcrEnabled, setAutoOcrEnabled] = useState(true);
  const [pageQuery, setPageQuery] = useState("");
  const [leftTab, setLeftTab] = useState<"pages" | "layers">("pages");
  const [isMiddlePanning, setIsMiddlePanning] = useState(false);
  const [discardConfirmationOpen, setDiscardConfirmationOpen] = useState(false);
  const snapshot = history[historyIndex];
  const selectedEdit = snapshot.edits.find((edit) => edit.id === selectedEditId) ?? null;

  useEffect(() => {
    document.documentElement.lang = language;
    try {
      localStorage.setItem(INTERFACE_LANGUAGE_STORAGE_KEY, language);
    } catch {
      // The switch remains usable when browser storage is unavailable.
    }
  }, [language]);

  const changeInterfaceLanguage = (nextLanguage: InterfaceLanguage) => {
    const nextUi = getUiCopy(nextLanguage);
    setLanguage(nextLanguage);
    setMessage(busy ? nextUi.processing : pdf ? nextUi.pagesLoaded(pdf.pages.length) : nextUi.ready);
  };

  useEffect(() => {
    let checking = false;
    const checkForNewBuild = async () => {
      if (checking || import.meta.env.DEV) return;
      checking = true;
      try {
        const response = await fetch(`/?build-check=${Date.now()}`, { cache: "no-store" });
        const html = await response.text();
        const latestSource = html.match(/<script[^>]+src="([^"]*\/assets\/index-[^"]+\.js)"/)?.[1];
        const currentSource = document.querySelector<HTMLScriptElement>('script[type="module"][src]')?.getAttribute("src");
        if (latestSource && currentSource && new URL(latestSource, location.href).href !== new URL(currentSource, location.href).href) {
          location.reload();
        }
      } catch {
        // An offline editor remains usable; the next successful check will refresh it.
      } finally {
        checking = false;
      }
    };
    const onVisibilityChange = () => {
      if (document.visibilityState === "visible") void checkForNewBuild();
    };
    const interval = window.setInterval(() => void checkForNewBuild(), 45_000);
    document.addEventListener("visibilitychange", onVisibilityChange);
    void checkForNewBuild();
    return () => {
      window.clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibilityChange);
    };
  }, []);

  const commit = useCallback((next: EditorSnapshot) => {
    setHistory((current) => [...current.slice(0, historyIndex + 1), next]);
    setHistoryIndex((index) => index + 1);
  }, [historyIndex]);

  const loadBytes = useCallback(async (bytes: Uint8Array, name: string, restoredState: StoredEditorState | null = null) => {
    setBusy(true);
    setMessage(ui.loadingPdf);
    try {
      const loaded = await loadPdf(bytes, name);
      if (!restoredState) {
        if (clearWorkspacePromiseRef.current) await clearWorkspacePromiseRef.current;
        clearWorkspacePromiseRef.current = null;
        await saveStoredPdf(bytes, name).catch((error) => console.error(error));
      }
      const restoredSnapshot = restoredState?.snapshot ?? EMPTY_SNAPSHOT;
      const restoredPage = clamp(restoredState?.activePage ?? 0, 0, Math.max(0, loaded.pages.length - 1));
      const restoredSelection = restoredState?.selectedEditId && restoredSnapshot.edits.some((edit) => edit.id === restoredState.selectedEditId)
        ? restoredState.selectedEditId
        : null;
      setPdf(loaded);
      setActivePage(restoredPage);
      setZoom(restoredState?.zoom ?? 0.82);
      setHistory([restoredSnapshot]);
      setHistoryIndex(0);
      setSelectedEditId(restoredSelection);
      setOcrByPage(restoredState?.fontAnalysisVersion === 2 ? (restoredState.ocrByPage ?? {}) : {});
      setOcrLanguages(restoredState?.ocrLanguages ?? ["slk", "ces", "eng"]);
      setAutoOcrEnabled(restoredState?.autoOcrEnabled ?? true);
      setLeftTab(restoredState?.leftTab ?? "pages");
      autoOcrStartedRef.current.clear();
      setRightTab(restoredState?.rightTab ?? (loaded.pages[0]?.isScanned ? "ocr" : "edit"));
      setMessage(ui.pagesLoaded(loaded.pages.length));
    } catch (error) {
      console.error(error);
      setMessage(ui.pdfOpenFailed);
    } finally {
      setBusy(false);
    }
  }, [ui]);

  useEffect(() => {
    if (restoreStartedRef.current) return;
    restoreStartedRef.current = true;
    void (async () => {
      try {
        const stored = await loadStoredWorkspace();
        if (stored) await loadBytes(new Uint8Array(stored.pdf.bytes), stored.pdf.name, stored.state);
      } catch (error) {
        console.error(error);
        await clearStoredWorkspace().catch(() => undefined);
      } finally {
        setPersistenceReady(true);
      }
    })();
  }, [loadBytes]);

  useEffect(() => {
    if (!persistenceReady || !pdf) return;
    const timeout = window.setTimeout(() => {
      void saveStoredEditorState({
        version: 1,
        fontAnalysisVersion: 2,
        snapshot,
        activePage,
        zoom,
        selectedEditId,
        rightTab,
        leftTab,
        ocrByPage,
        ocrLanguages,
        autoOcrEnabled,
      }).catch((error) => console.error(error));
    }, 80);
    return () => window.clearTimeout(timeout);
  }, [activePage, autoOcrEnabled, leftTab, ocrByPage, ocrLanguages, pdf, persistenceReady, rightTab, selectedEditId, snapshot, zoom]);

  const discardCurrentPdf = useCallback(() => {
    setDiscardConfirmationOpen(false);
    clearWorkspacePromiseRef.current = clearStoredWorkspace().catch((error) => console.error(error));
    setPdf(null);
    setHistory([EMPTY_SNAPSHOT]);
    setHistoryIndex(0);
    setSelectedEditId(null);
    setOcrByPage({});
    setActivePage(0);
    autoOcrStartedRef.current.clear();
    fileInputRef.current?.click();
  }, []);

  const addNewPdf = useCallback(() => {
    if (pdf) {
      setDiscardConfirmationOpen(true);
      return;
    }
    fileInputRef.current?.click();
  }, [pdf]);

  const openFile = useCallback((file: File) => {
    if (!file.name.toLowerCase().endsWith(".pdf") && file.type !== "application/pdf") {
      setMessage(ui.choosePdf);
      return;
    }
    void file.arrayBuffer().then((buffer) => loadBytes(new Uint8Array(buffer), file.name));
  }, [loadBytes, ui]);

  useEffect(() => {
    const handler = (event: KeyboardEvent) => {
      const command = event.metaKey || event.ctrlKey;
      if (command && event.key.toLowerCase() === "z") {
        event.preventDefault();
        if (event.shiftKey) setHistoryIndex((index) => Math.min(history.length - 1, index + 1));
        else setHistoryIndex((index) => Math.max(0, index - 1));
      }
      if (command && event.key.toLowerCase() === "o") {
        event.preventDefault();
        addNewPdf();
      }
      if ((event.key === "Backspace" || event.key === "Delete") && selectedEditId && !(event.target instanceof HTMLInputElement) && !(event.target instanceof HTMLTextAreaElement)) {
        event.preventDefault();
        commit({ ...snapshot, edits: snapshot.edits.filter((edit) => edit.id !== selectedEditId) });
        setSelectedEditId(null);
      }
      if (event.key === "Escape") {
        if (discardConfirmationOpen) {
          setDiscardConfirmationOpen(false);
          return;
        }
        setSelectedEditId(null);
        setTool("select");
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [addNewPdf, commit, discardConfirmationOpen, history.length, selectedEditId, snapshot]);

  const embeddedFonts = useMemo<FontUsage[]>(() => pdf ? aggregateFonts(pdf.pages.flatMap((page) => page.textItems)) : [], [pdf]);
  const ocrFonts = useMemo<FontUsage[]>(() => aggregateOcrFonts(Object.values(ocrByPage).flat()), [ocrByPage]);
  const analyzedFonts = useMemo<FontUsage[]>(() => [...embeddedFonts, ...ocrFonts], [embeddedFonts, ocrFonts]);
  const visiblePages = useMemo(() => {
    if (!pdf) return [];
    const query = pageQuery.trim().toLowerCase();
    return pdf.pages.filter((page) => !query || `${page.index + 1}`.includes(query) || (page.isScanned && "sken scan ocr".includes(query)));
  }, [pageQuery, pdf]);

  const createEdit = (edit: PdfEdit) => {
    commit({ ...snapshot, edits: [...snapshot.edits, edit] });
    setSelectedEditId(edit.id);
    setTool("select");
    setRightTab("edit");
    if (edit.kind !== "text") setLeftTab("layers");
  };

  const updateEdit = (id: string, patch: Partial<PdfEdit>) => {
    commit({
      ...snapshot,
      edits: snapshot.edits.map((edit) => {
        if (edit.id !== id) return edit;
        const next = { ...edit, ...patch };
        if (next.source === "native" || next.source === "ocr") next.dirty = true;
        if (patch.fontSize !== undefined) next.preferredFontSize = patch.fontSize;
        if (next.kind === "text" && next.autoFit) {
          next.fontSize = measuredFontSize({
            text: next.text || "",
            width: next.width,
            height: next.height,
            preferredSize: next.preferredFontSize || next.fontSize || 14,
            fontFamily: next.fontFamily,
            fontWeight: next.fontWeight,
            fontStyle: next.fontStyle,
            embeddedFontFamily: next.embeddedFontFamily,
          });
        }
        return next;
      }),
    });
  };

  const sourceToEdit = (source: TextOverlay) => {
    const existing = snapshot.edits.find((edit) => edit.sourceId === source.id);
    if (existing) {
      if (existing.fontAutoMatch !== false) {
        const typography = source.source === "ocr"
          ? ocrTypographyPatch(source, existing.width, existing.height)
          : nativeTypographyPatch(source, existing.width, existing.height);
        commit({
          ...snapshot,
          edits: snapshot.edits.map((edit) => edit.id === existing.id ? {
            ...edit,
            ...typography,
            originalText: edit.originalText ?? source.text,
            dirty: edit.dirty ?? ((edit.text || "") !== source.text),
          } : edit),
        });
      }
      setSelectedEditId(existing.id);
      setRightTab("edit");
      return;
    }
    const keepsColoredForeground = Boolean(source.foregroundImageDataUrl);
    const width = keepsColoredForeground ? Math.max(8, source.width) : Math.max(16, source.width + 4);
    const height = keepsColoredForeground ? Math.max(8, source.height) : Math.max(12, source.height * 1.15 + 2);
    const automaticTypography = source.source === "ocr"
      ? ocrTypographyPatch(source, width, height)
      : nativeTypographyPatch(source, width, height);
    createEdit({
      id: makeId(), pageIndex: source.pageIndex, kind: "text",
      x: Math.max(0, source.x - (keepsColoredForeground ? 0 : 1)), y: Math.max(0, source.y - (keepsColoredForeground ? 0 : 1)), width, height,
      text: source.text, ...automaticTypography, color: "#111318", opacity: 1,
      source: source.source, sourceId: source.id, originalText: source.text, dirty: false,
      maskOriginal: true, autoFit: true, foregroundImageDataUrl: source.foregroundImageDataUrl,
    });
  };

  const runOcr = useCallback(async (pageIndex = activePage, automatic = false) => {
    if (!pdf || ocrLanguages.length === 0) return;
    setBusy(true);
    setOcrProgress({ status: ui.preparingPage, progress: 0 });
    setMessage(automatic ? ui.automaticOcr(pageIndex + 1) : ui.ocrPage(pageIndex + 1));
    try {
      const rendered = await renderPageForOcr(pdf.document, pageIndex, 3.4);
      const { recognizePage } = await import("./ocr");
      const items = await recognizePage(rendered.canvas, pageIndex, rendered.scale, ocrLanguages, (progress) => {
        setOcrProgress({ ...progress, status: ui.ocrProgress(progress.status) });
      });
      setOcrByPage((current) => ({ ...current, [pageIndex]: items }));
      const itemsById = new Map(items.map((item) => [item.id, item]));
      setHistory((current) => current.map((entry) => ({
        ...entry,
        edits: entry.edits.map((edit) => {
          if (edit.source !== "ocr" || edit.fontAutoMatch === false || !edit.sourceId) return edit;
          const source = itemsById.get(edit.sourceId);
          return source ? {
            ...edit,
            ...ocrTypographyPatch(source, edit.width, edit.height),
            originalText: edit.originalText ?? source.text,
            dirty: edit.dirty ?? ((edit.text || "") !== source.text),
            foregroundImageDataUrl: source.foregroundImageDataUrl ?? edit.foregroundImageDataUrl,
          } : edit;
        }),
      })));
      setMessage(items.length ? ui.ocrFound(items.length) : ui.ocrNotFound);
      setTool("select");
    } catch (error) {
      console.error(error);
      setMessage(ui.ocrFailed);
    } finally {
      setBusy(false);
      setOcrProgress(null);
    }
  }, [activePage, ocrLanguages, pdf, ui]);

  useEffect(() => {
    const page = pdf?.pages[activePage];
    if (!pdf || busy || !autoOcrEnabled || !page?.isScanned || ocrByPage[activePage] || autoOcrStartedRef.current.has(activePage)) return;
    autoOcrStartedRef.current.add(activePage);
    setRightTab("ocr");
    void runOcr(activePage, true);
  }, [activePage, autoOcrEnabled, busy, ocrByPage, pdf, runOcr]);

  const exportPdf = async () => {
    if (!pdf) return;
    setExporting(true);
    setMessage(ui.creatingPdf);
    try {
      const { downloadPdf, exportEditedPdf } = await import("./exportPdf");
      const sourceTextById = new Map([
        ...pdf.pages.flatMap((page) => page.textItems),
        ...Object.values(ocrByPage).flat(),
      ].map((item) => [item.id, item.text]));
      const editsForExport = snapshot.edits.map((edit) => edit.originalText !== undefined
        ? edit
        : { ...edit, originalText: edit.sourceId ? sourceTextById.get(edit.sourceId) : undefined });
      const bytes = await exportEditedPdf(pdf.bytes, pdf.pages, editsForExport, snapshot.deletedPages, pdf.embeddedFonts);
      downloadPdf(bytes, pdf.name);
      setMessage(ui.pdfDownloaded);
    } catch (error) {
      console.error(error);
      setMessage(ui.exportFailed);
    } finally {
      setExporting(false);
    }
  };

  const toggleDeletedPage = () => {
    if (!pdf || pdf.pages.length - snapshot.deletedPages.length <= 1 && !snapshot.deletedPages.includes(activePage)) {
      setMessage(ui.pdfNeedsPage);
      return;
    }
    const deleted = snapshot.deletedPages.includes(activePage);
    const deletedPages = deleted
      ? snapshot.deletedPages.filter((index) => index !== activePage)
      : [...snapshot.deletedPages, activePage];
    commit({ ...snapshot, deletedPages });
    setMessage(deleted ? ui.pageRestored(activePage + 1) : ui.pageDeleted(activePage + 1));
    if (!deleted) {
      const next = pdf.pages.find((page) => page.index !== activePage && !deletedPages.includes(page.index));
      if (next) setActivePage(next.index);
    }
  };

  const activePageInfo = pdf?.pages[activePage] ?? null;
  const ocrItems = ocrByPage[activePage] ?? [];

  const handleStageWheel = (event: ReactWheelEvent<HTMLDivElement>) => {
    const scroller = stageScrollRef.current;
    if (!scroller || event.ctrlKey || event.metaKey) return;
    const unit = event.deltaMode === 1 ? 32 : event.deltaMode === 2 ? scroller.clientHeight : 1;
    let deltaX = event.deltaX * unit;
    let deltaY = event.deltaY * unit;
    if (event.shiftKey && Math.abs(deltaX) < Math.abs(deltaY)) {
      deltaX = deltaY;
      deltaY = 0;
    }
    if (deltaX === 0 && deltaY === 0) return;
    event.preventDefault();
    scroller.scrollLeft += deltaX;
    scroller.scrollTop += deltaY;
  };

  const beginMiddlePan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = stageScrollRef.current;
    if (!scroller || event.button !== 1) return;
    event.preventDefault();
    event.stopPropagation();
    scroller.setPointerCapture(event.pointerId);
    middlePanSessionRef.current = {
      pointerId: event.pointerId,
      startClientX: event.clientX,
      startClientY: event.clientY,
      startScrollLeft: scroller.scrollLeft,
      startScrollTop: scroller.scrollTop,
    };
    setIsMiddlePanning(true);
  };

  const moveMiddlePan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = stageScrollRef.current;
    const session = middlePanSessionRef.current;
    if (!scroller || !session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    scroller.scrollLeft = session.startScrollLeft - (event.clientX - session.startClientX);
    scroller.scrollTop = session.startScrollTop - (event.clientY - session.startClientY);
  };

  const finishMiddlePan = (event: ReactPointerEvent<HTMLDivElement>) => {
    const scroller = stageScrollRef.current;
    const session = middlePanSessionRef.current;
    if (!scroller || !session || session.pointerId !== event.pointerId) return;
    event.preventDefault();
    event.stopPropagation();
    if (scroller.hasPointerCapture(event.pointerId)) scroller.releasePointerCapture(event.pointerId);
    middlePanSessionRef.current = null;
    setIsMiddlePanning(false);
  };

  return (
    <div
      className={`app-shell ${pdf ? "has-document" : ""}`}
      onDragOver={(event) => { event.preventDefault(); event.dataTransfer.dropEffect = "copy"; }}
      onDrop={(event) => { event.preventDefault(); const file = event.dataTransfer.files[0]; if (file) openFile(file); }}
    >
      <input ref={fileInputRef} hidden type="file" accept="application/pdf,.pdf" onChange={(event) => { const file = event.target.files?.[0]; if (file) openFile(file); event.target.value = ""; }} />
      <header className="top-bar">
        <div className="brand"><span className="brand-glyph"><FileText /></span><span>PDF LITE</span></div>
        <div className="toolbar-separator" />
        <div className="toolbar-cluster">
          <IconButton label={ui.addNewPdf} onClick={addNewPdf}><FilePlus2 /></IconButton>
          <IconButton label={ui.exportPdf} onClick={() => void exportPdf()} disabled={!pdf || exporting}>{exporting ? <LoaderCircle className="spin" /> : <Download />}</IconButton>
        </div>
        <div className="toolbar-separator" />
        <div className="toolbar-cluster">
          <IconButton label={ui.undo} onClick={() => setHistoryIndex((index) => Math.max(0, index - 1))} disabled={historyIndex === 0}><Undo2 /></IconButton>
          <IconButton label={ui.redo} onClick={() => setHistoryIndex((index) => Math.min(history.length - 1, index + 1))} disabled={historyIndex >= history.length - 1}><Redo2 /></IconButton>
        </div>
        <div className="toolbar-separator" />
        <div className="toolbar-cluster tool-cluster">
          <IconButton label={ui.selectTool} active={tool === "select"} onClick={() => setTool("select")} disabled={!pdf}><MousePointer2 /></IconButton>
          <IconButton label={ui.addText} active={tool === "text"} onClick={() => setTool("text")} disabled={!pdf}><Type /></IconButton>
          <IconButton label={ui.highlightTool} active={tool === "highlight"} onClick={() => setTool("highlight")} disabled={!pdf}><span className="tool-swatch tool-swatch-highlight" /></IconButton>
          <IconButton label={ui.redactTool} active={tool === "redact"} onClick={() => setTool("redact")} disabled={!pdf}><span className="tool-swatch tool-swatch-redact" /></IconButton>
          <IconButton label={ui.ocrActivePage} active={rightTab === "ocr"} onClick={() => setRightTab("ocr")} disabled={!pdf}><ScanText /></IconButton>
        </div>
        <div className="document-title">{pdf && <><FileCheck2 /> {pdf.name}</>}</div>
        <div className="toolbar-cluster zoom-cluster">
          <IconButton label={ui.zoomOut} onClick={() => setZoom((value) => clamp(value - 0.1, 0.3, 2.4))} disabled={!pdf}><ZoomOut /></IconButton>
          <span className="zoom-value">{Math.round(zoom * 100)}%</span>
          <IconButton label={ui.zoomIn} onClick={() => setZoom((value) => clamp(value + 0.1, 0.3, 2.4))} disabled={!pdf}><ZoomIn /></IconButton>
        </div>
        <div className="language-switch" role="group" aria-label={ui.languageControl}>
          <button type="button" className={language === "sk" ? "is-active" : ""} aria-pressed={language === "sk"} onClick={() => changeInterfaceLanguage("sk")}>SVK</button>
          <span>/</span>
          <button type="button" className={language === "en" ? "is-active" : ""} aria-pressed={language === "en"} onClick={() => changeInterfaceLanguage("en")}>ENG</button>
        </div>
      </header>

      {!pdf ? <Welcome onOpen={addNewPdf} busy={busy} ui={ui} /> : <>
        <aside className="left-panel panel">
          <div className="panel-tabs">
            <button type="button" className={`panel-tab ${leftTab === "pages" ? "is-active" : ""}`} onClick={() => setLeftTab("pages")}>{ui.pages}</button>
            <button type="button" className={`panel-tab ${leftTab === "layers" ? "is-active" : ""}`} onClick={() => setLeftTab("layers")}>{ui.layers}</button>
            <span>{leftTab === "pages" ? pdf.pages.length - snapshot.deletedPages.length : snapshot.edits.filter((edit) => edit.pageIndex === activePage).length}</span>
          </div>
          {leftTab === "pages" ? <>
            <label className="search-field"><Search /><input value={pageQuery} onChange={(event) => setPageQuery(event.target.value)} placeholder={ui.searchPage} /></label>
            <div className="page-list">
              {visiblePages.map((page) => <Thumbnail key={page.index} document={pdf.document} page={page} active={page.index === activePage} deleted={snapshot.deletedPages.includes(page.index)} onClick={() => setActivePage(page.index)} ui={ui} />)}
            </div>
          </> : <LayerList
            edits={snapshot.edits}
            pageIndex={activePage}
            selectedEditId={selectedEditId}
            ui={ui}
            onSelect={(edit) => { setActivePage(edit.pageIndex); setSelectedEditId(edit.id); setTool("select"); setRightTab("edit"); }}
            onToggleVisibility={(edit) => { updateEdit(edit.id, { hidden: !edit.hidden }); setMessage(edit.hidden ? ui.layerShown : ui.layerHidden); }}
          />}
        </aside>

        <main className="editor-stage">
          <div className="stage-ruler stage-ruler-x" />
          <div className="stage-ruler stage-ruler-y" />
          <div
            ref={stageScrollRef}
            className={`stage-scroll ${isMiddlePanning ? "is-middle-panning" : ""}`}
            data-testid="stage-scroll"
            onWheel={handleStageWheel}
            onPointerDown={(event) => {
              if (event.button === 0 && tool === "select") setSelectedEditId(null);
            }}
            onPointerDownCapture={beginMiddlePan}
            onPointerMoveCapture={moveMiddlePan}
            onPointerUpCapture={finishMiddlePan}
            onPointerCancelCapture={finishMiddlePan}
            onAuxClick={(event) => { if (event.button === 1) event.preventDefault(); }}
          >
            {activePageInfo && !snapshot.deletedPages.includes(activePageInfo.index) && (
              <PageCanvas
                document={pdf.document}
                page={activePageInfo}
                zoom={zoom}
                tool={tool}
                edits={snapshot.edits}
                ocrItems={ocrItems}
                selectedEditId={selectedEditId}
                onTextSource={sourceToEdit}
                onSelectEdit={setSelectedEditId}
                onCreateEdit={createEdit}
                onUpdateEdit={updateEdit}
                ui={ui}
              />
            )}
          </div>
        </main>

        <aside className="right-panel panel">
          <div className="panel-tabs inspector-tabs">
            <button type="button" className={`panel-tab ${rightTab === "edit" ? "is-active" : ""}`} onClick={() => setRightTab("edit")}>{ui.edit}</button>
            <button type="button" className={`panel-tab ${rightTab === "fonts" ? "is-active" : ""}`} onClick={() => setRightTab("fonts")}>{ui.fonts}</button>
            <button type="button" className={`panel-tab ${rightTab === "ocr" ? "is-active" : ""}`} onClick={() => setRightTab("ocr")}>OCR</button>
          </div>

          {rightTab === "edit" && <div className="inspector-scroll">
            {selectedEdit ? <>
              <section className="inspector-section">
                <div className="section-heading"><h3>{selectedEdit.kind === "text" ? ui.text : selectedEdit.kind === "highlight" ? ui.highlight : ui.redaction}</h3><StatusPill>{ui.sourceLabel(selectedEdit.source)}</StatusPill></div>
                {selectedEdit.kind === "text" && <label className="stacked-field"><span>{ui.content}</span><textarea value={selectedEdit.text || ""} onChange={(event) => updateEdit(selectedEdit.id, { text: event.target.value })} /></label>}
                {selectedEdit.kind === "text" && <div className="field-grid">
                  <label><span>{ui.exportFont}</span><select aria-label={ui.exportFont} value={selectedEdit.fontFamily} onChange={(event) => updateEdit(selectedEdit.id, { fontFamily: event.target.value as PdfEdit["fontFamily"], embeddedFontId: undefined, embeddedFontFamily: undefined, fontAutoMatch: false })}><option>Inter</option><option>Liberation Sans</option><option>Liberation Serif</option><option>Arial</option><option>Helvetica</option><option>Times Roman</option><option>Courier</option></select></label>
                  <label><span>{selectedEdit.autoFit !== false ? ui.maxSize : ui.size}</span><input type="number" min="5" max="96" value={Math.round(selectedEdit.preferredFontSize || selectedEdit.fontSize || 14)} onChange={(event) => updateEdit(selectedEdit.id, { fontSize: Number(event.target.value), fontAutoMatch: false })} /></label>
                </div>}
                {selectedEdit.kind === "text" && <div className="field-grid typography-grid">
                  <label><span>{ui.weight}</span><select aria-label={ui.fontWeightLabel} value={selectedEdit.fontWeight || 400} onChange={(event) => updateEdit(selectedEdit.id, { fontWeight: Number(event.target.value) as PdfEdit["fontWeight"], embeddedFontId: undefined, embeddedFontFamily: undefined, fontAutoMatch: false })}><option value="400">Regular</option><option value="700">Bold</option></select></label>
                  <label><span>{ui.fontStyle}</span><select aria-label={ui.fontStyle} value={selectedEdit.fontStyle || "normal"} onChange={(event) => updateEdit(selectedEdit.id, { fontStyle: event.target.value as PdfEdit["fontStyle"], embeddedFontId: undefined, embeddedFontFamily: undefined, fontAutoMatch: false })}><option value="normal">Normal</option><option value="italic">Italic</option></select></label>
                </div>}
                {selectedEdit.kind === "text" && selectedEdit.source === "ocr" && <div className="font-match-status"><Check /><span>{ui.matchedFont}</span><strong>{Math.round((selectedEdit.fontMatchConfidence || 0.55) * 100)}%</strong></div>}
                <div className="field-grid">
                  <label><span>{ui.color}</span><input type="color" value={selectedEdit.color} onChange={(event) => updateEdit(selectedEdit.id, { color: event.target.value })} /></label>
                  <label><span>{ui.opacity}</span><input type="number" min="0.1" max="1" step="0.1" value={selectedEdit.opacity} onChange={(event) => updateEdit(selectedEdit.id, { opacity: Number(event.target.value) })} /></label>
                </div>
                {selectedEdit.kind === "text" && <label className="check-row"><input type="checkbox" checked={selectedEdit.maskOriginal} onChange={(event) => updateEdit(selectedEdit.id, { maskOriginal: event.target.checked })} /><span>{ui.coverOriginal}</span></label>}
                {selectedEdit.kind === "text" && <label className="check-row"><input type="checkbox" checked={selectedEdit.autoFit !== false} onChange={(event) => updateEdit(selectedEdit.id, { autoFit: event.target.checked })} /><span>{ui.autoFit}</span>{selectedEdit.autoFit !== false && <small className="fit-size">{(selectedEdit.fontSize || 0).toFixed(1)} pt</small>}</label>}
              </section>
              <section className="inspector-section">
                <h3>{ui.position}</h3>
                <div className="field-grid compact"><label><span>X</span><input type="number" value={Math.round(selectedEdit.x)} onChange={(event) => updateEdit(selectedEdit.id, { x: Number(event.target.value) })} /></label><label><span>Y</span><input type="number" value={Math.round(selectedEdit.y)} onChange={(event) => updateEdit(selectedEdit.id, { y: Number(event.target.value) })} /></label><label><span>W</span><input type="number" value={Math.round(selectedEdit.width)} onChange={(event) => updateEdit(selectedEdit.id, { width: Number(event.target.value) })} /></label><label><span>H</span><input type="number" value={Math.round(selectedEdit.height)} onChange={(event) => updateEdit(selectedEdit.id, { height: Number(event.target.value) })} /></label></div>
                <label className="rotation-field"><span>{ui.rotation}</span><input type="range" min="-180" max="180" step="1" value={selectedEdit.rotation || 0} onChange={(event) => updateEdit(selectedEdit.id, { rotation: Number(event.target.value) })} /><input aria-label={ui.rotationDegrees} type="number" min="-180" max="180" value={Math.round(selectedEdit.rotation || 0)} onChange={(event) => updateEdit(selectedEdit.id, { rotation: Number(event.target.value) })} /><small>°</small></label>
              </section>
              <button type="button" className="danger-button" onClick={() => { commit({ ...snapshot, edits: snapshot.edits.filter((edit) => edit.id !== selectedEdit.id) }); setSelectedEditId(null); }}><Trash2 /> {ui.deleteEdit}</button>
            </> : <div className="empty-inspector"><MousePointer2 /><strong>{ui.selectEdit}</strong><span>{ui.selectEditHint}</span></div>}
            <section className="inspector-section page-actions"><h3>{ui.activePage}</h3><button type="button" className="secondary-button small" onClick={toggleDeletedPage}><Trash2 /> {ui.deleteRestorePage}</button></section>
          </div>}

          {rightTab === "fonts" && <div className="inspector-scroll">
            <section className="font-summary"><div><Type /><strong>{analyzedFonts.length}</strong><span>{ui.fontEstimates}</span></div><div><FileText /><strong>{pdf.pages.filter((page) => page.isScanned).length}</strong><span>{ui.scannedPages}</span></div></section>
            <section className="inspector-section info-box"><Info /><p>{ui.fontAnalyzerInfo}</p></section>
            <div className="font-list">
              {analyzedFonts.map((font) => <article className="font-card" key={font.id}><div className="font-preview" style={{ fontFamily: fontFamilyCss(font.family as PdfEdit["fontFamily"]), fontWeight: font.fontWeight || 400, fontStyle: font.fontStyle || "normal" }}>Aa</div><div><strong>{font.family}{font.fontWeight === 700 ? " Bold" : ""}{font.fontStyle === "italic" ? " Italic" : ""}</strong><code>{font.source === "ocr" ? ui.ocrEstimate(Math.round((font.confidence || 0.55) * 100)) : font.internalName}</code><span>{font.minSize.toFixed(1)}–{font.maxSize.toFixed(1)} pt · {ui.characters(font.characters)}</span><small>{ui.fontPages(font.pages)}</small></div></article>)}
              {analyzedFonts.length === 0 && <div className="empty-inspector"><Type /><strong>{ui.awaitingAnalysis}</strong><span>{ui.awaitingAnalysisHint}</span></div>}
            </div>
          </div>}

          {rightTab === "ocr" && <div className="inspector-scroll">
            <section className="ocr-hero"><ScanText /><h2>{ui.ocrScans}</h2><p>{ui.ocrScansInfo}</p></section>
            <section className="inspector-section auto-ocr-setting"><label className="check-row"><input type="checkbox" checked={autoOcrEnabled} onChange={(event) => setAutoOcrEnabled(event.target.checked)} /><span>{ui.automaticOpenScan}</span>{autoOcrEnabled && <Check />}</label></section>
            <section className="inspector-section"><h3>{ui.documentLanguages}</h3>{[{ code: "slk", label: ui.slovak }, { code: "ces", label: ui.czech }, { code: "eng", label: ui.english }].map((ocrLanguage) => <label className="check-row" key={ocrLanguage.code}><input type="checkbox" checked={ocrLanguages.includes(ocrLanguage.code)} onChange={(event) => setOcrLanguages((current) => event.target.checked ? [...current, ocrLanguage.code] : current.filter((code) => code !== ocrLanguage.code))} /><span>{ocrLanguage.label}</span>{ocrLanguages.includes(ocrLanguage.code) && <Check />}</label>)}</section>
            <section className="inspector-section"><div className="scan-status"><span>{ui.page(activePage + 1)}</span>{activePageInfo?.isScanned ? <StatusPill tone="amber"><FileScan /> {ui.scan}</StatusPill> : <StatusPill tone="green"><FileCheck2 /> {ui.textLayer}</StatusPill>}</div><button type="button" className="primary-button full" disabled={busy || ocrLanguages.length === 0} onClick={() => void runOcr()}>{busy ? <LoaderCircle className="spin" /> : <ScanText />} {ocrItems.length ? ui.runOcrAgain : ui.runOcr}</button>{ocrProgress && <div className="progress"><span style={{ width: `${ocrProgress.progress * 100}%` }} /><small>{ocrProgress.status} · {Math.round(ocrProgress.progress * 100)}%</small></div>}{ocrItems.length > 0 && <p className="success-note"><Check /> {ui.rowsReady(ocrItems.length)}</p>}</section>
            {ocrItems.length > 0 && <section className="ocr-results" aria-label={ui.recognizedText}>{ocrItems.map((item) => <button type="button" className="ocr-result-button" key={item.id} onClick={() => sourceToEdit(item)}><span style={{ fontFamily: fontFamilyCss(item.fontFamily as PdfEdit["fontFamily"]), fontWeight: item.fontWeight || 400, fontStyle: item.fontStyle || "normal" }}>{item.text}</span><small>{item.fontFamily} {item.fontWeight === 700 ? "Bold" : "Regular"}{item.fontStyle === "italic" ? " Italic" : ""} · {item.fontSize.toFixed(1)} pt · {Math.round((item.fontMatchConfidence || 0.55) * 100)}%</small></button>)}</section>}
            <section className="inspector-section info-box amber"><Info /><p>{ui.firstOcrInfo}</p></section>
          </div>}
        </aside>
      </>}

      {discardConfirmationOpen && <div
        className="confirmation-backdrop"
        onMouseDown={(event) => { if (event.target === event.currentTarget) setDiscardConfirmationOpen(false); }}
      >
        <section
          className="confirmation-dialog"
          role="dialog"
          aria-modal="true"
          aria-labelledby="discard-pdf-title"
          aria-describedby="discard-pdf-message"
        >
          <div className="confirmation-icon"><Trash2 /></div>
          <div className="confirmation-copy">
            <h2 id="discard-pdf-title">{ui.discardPdfTitle}</h2>
            <p id="discard-pdf-message">{ui.discardPdfMessage}</p>
          </div>
          <div className="confirmation-actions">
            <button type="button" className="secondary-button" autoFocus onClick={() => setDiscardConfirmationOpen(false)}>{ui.cancel}</button>
            <button type="button" className="danger-button" onClick={discardCurrentPdf}><Trash2 /> {ui.discardAndContinue}</button>
          </div>
        </section>
      </div>}

      <footer className="status-bar">
        <span className="status-message">{busy && <LoaderCircle className="spin" />}{message}</span>
        <span className="powered-by" aria-label="Powered by LUBOSH">
          <span className="powered-by-copy">Powered by</span>
          <span className="powered-by-brand">LUBOSH</span>
        </span>
        <span className="status-right">{pdf && <>{ui.editsAndRows(snapshot.edits.length, ocrItems.length)}</>}</span>
      </footer>
    </div>
  );
}
